import React, { useState, useEffect, useRef } from 'react';
import { Play, Pause, RotateCcw, FastForward, Rewind, Info } from 'lucide-react';

/**
 * Simulation Engine Component
 * 
 * Advanced A2A simulation runner with:
 * - Step-by-step execution
 * - Visual message flow display
 * - Agent communication log
 * - Current step highlighting
 * - Pause/resume/reset controls
 * - Speed controls
 * - Educational tooltips
 */
const SimulationEngine = ({ simulation, onComplete }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [messageLog, setMessageLog] = useState([]);
  const [speed, setSpeed] = useState(1); // 0.5x, 1x, 2x, 4x
  const [showTooltip, setShowTooltip] = useState(null);
  const [agentStates, setAgentStates] = useState({});
  
  const intervalRef = useRef(null);
  const totalSteps = simulation.messages.length;

  // Initialize agent states
  useEffect(() => {
    const initialStates = {};
    simulation.agents.forEach(agent => {
      initialStates[agent.id] = {
        name: agent.name,
        type: agent.type,
        status: 'idle',
        currentMessage: null
      };
    });
    setAgentStates(initialStates);
  }, [simulation]);

  // Auto-play simulation
  useEffect(() => {
    if (isPlaying && currentStep < totalSteps) {
      const message = simulation.messages[currentStep];
      const delay = (message.duration || 1000) / speed;

      intervalRef.current = setTimeout(() => {
        executeStep(currentStep);
        setCurrentStep(currentStep + 1);
      }, delay);
    } else if (currentStep >= totalSteps) {
      setIsPlaying(false);
      if (onComplete) onComplete();
    }

    return () => {
      if (intervalRef.current) clearTimeout(intervalRef.current);
    };
  }, [isPlaying, currentStep, speed, totalSteps]);

  const executeStep = (stepIndex) => {
    const message = simulation.messages[stepIndex];
    
    // Update agent states
    setAgentStates(prev => ({
      ...prev,
      [message.sender]: {
        ...prev[message.sender],
        status: 'sending',
        currentMessage: message
      },
      [message.receiver]: {
        ...prev[message.receiver],
        status: 'receiving',
        currentMessage: message
      }
    }));

    // Add to message log
    setMessageLog(prev => [...prev, {
      stepNumber: stepIndex + 1,
      timestamp: new Date().toLocaleTimeString(),
      message: message,
      status: 'sent'
    }]);

    // Reset agent states after a brief moment
    setTimeout(() => {
      setAgentStates(prev => ({
        ...prev,
        [message.sender]: { ...prev[message.sender], status: 'idle' },
        [message.receiver]: { ...prev[message.receiver], status: 'idle' }
      }));
    }, 500);
  };

  const handlePlayPause = () => {
    if (currentStep >= totalSteps) {
      handleReset();
    }
    setIsPlaying(!isPlaying);
  };

  const handleReset = () => {
    setIsPlaying(false);
    setCurrentStep(0);
    setMessageLog([]);
    
    // Reset all agent states
    const resetStates = {};
    simulation.agents.forEach(agent => {
      resetStates[agent.id] = {
        name: agent.name,
        type: agent.type,
        status: 'idle',
        currentMessage: null
      };
    });
    setAgentStates(resetStates);
  };

  const handleStepForward = () => {
    if (currentStep < totalSteps) {
      executeStep(currentStep);
      setCurrentStep(currentStep + 1);
    }
  };

  const handleStepBackward = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      setMessageLog(prev => prev.slice(0, -1));
    }
  };

  const getAgentColor = (type) => {
    return type === 'client' 
      ? 'from-blue-500 to-indigo-600' 
      : 'from-purple-500 to-pink-600';
  };

  const getAgentIcon = (type) => {
    return type === 'client' ? '👤' : '⚡';
  };

  return (
    <div className="space-y-4">
      {/* Control Panel */}
      <div className="card p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-lg text-gray-900">{simulation.name}</h3>
          <div className="text-sm text-gray-600">
            Step {currentStep} / {totalSteps}
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-4">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-gradient-to-r from-indigo-600 to-purple-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(currentStep / totalSteps) * 100}%` }}
            />
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <button
              onClick={handleStepBackward}
              disabled={currentStep === 0 || isPlaying}
              className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Step Backward"
            >
              <Rewind className="w-5 h-5" />
            </button>

            <button
              onClick={handlePlayPause}
              className={`flex items-center space-x-2 px-6 py-2 rounded-lg font-medium transition-all ${
                isPlaying 
                  ? 'bg-red-600 text-white hover:bg-red-700' 
                  : 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-700 hover:to-purple-700'
              }`}
            >
              {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
              <span>{isPlaying ? 'Pause' : currentStep >= totalSteps ? 'Replay' : 'Play'}</span>
            </button>

            <button
              onClick={handleStepForward}
              disabled={currentStep >= totalSteps || isPlaying}
              className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Step Forward"
            >
              <FastForward className="w-5 h-5" />
            </button>

            <button
              onClick={handleReset}
              className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200"
              title="Reset"
            >
              <RotateCcw className="w-5 h-5" />
            </button>
          </div>

          {/* Speed Controls */}
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600">Speed:</span>
            {[0.5, 1, 2, 4].map(s => (
              <button
                key={s}
                onClick={() => setSpeed(s)}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                  speed === s
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {s}x
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Visual Agent Flow */}
      <div className="card p-6">
        <h3 className="font-bold text-gray-900 mb-4">Agent Communication Flow</h3>
        
        <div className="flex items-center justify-around mb-6">
          {simulation.agents.map(agent => {
            const state = agentStates[agent.id] || {};
            const isActive = state.status !== 'idle';

            return (
              <div key={agent.id} className="relative">
                <div 
                  className={`flex flex-col items-center transition-all duration-300 ${
                    isActive ? 'scale-110' : 'scale-100'
                  }`}
                >
                  <div className={`w-24 h-24 rounded-full bg-gradient-to-br ${getAgentColor(agent.type)} flex items-center justify-center text-4xl mb-3 shadow-lg ${
                    isActive ? 'ring-4 ring-indigo-300 animate-pulse-slow' : ''
                  }`}>
                    {getAgentIcon(agent.type)}
                  </div>
                  <div className="text-center">
                    <div className="font-bold text-gray-900">{agent.name}</div>
                    <div className="text-xs text-gray-500">{agent.type} agent</div>
                    <div className={`text-xs mt-1 font-medium ${
                      state.status === 'sending' ? 'text-blue-600' :
                      state.status === 'receiving' ? 'text-green-600' :
                      'text-gray-400'
                    }`}>
                      {state.status === 'sending' ? '📤 Sending' :
                       state.status === 'receiving' ? '📥 Receiving' :
                       '💤 Idle'}
                    </div>
                  </div>
                </div>

                {/* Capabilities Badge */}
                <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
                  <div className="badge bg-indigo-100 text-indigo-800 text-xs">
                    {agent.capabilities.length} capabilities
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Message Arrow Animation */}
        {currentStep > 0 && currentStep <= totalSteps && (
          <div className="flex items-center justify-center my-8 animate-fadeIn">
            <div className="flex items-center space-x-2 bg-indigo-50 px-6 py-3 rounded-lg border-2 border-indigo-200">
              <span className="text-2xl animate-bounce">💬</span>
              <div className="text-sm">
                <div className="font-bold text-indigo-900">
                  {simulation.messages[currentStep - 1]?.messageType}
                </div>
                <div className="text-indigo-600">
                  {simulation.messages[currentStep - 1]?.task}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Message Log */}
      <div className="card p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-gray-900">Message Log</h3>
          <span className="text-sm text-gray-600">{messageLog.length} messages</span>
        </div>

        <div className="space-y-2 max-h-96 overflow-y-auto">
          {messageLog.length === 0 ? (
            <div className="text-center text-gray-500 py-8 italic">
              No messages yet. Click Play to start the simulation.
            </div>
          ) : (
            messageLog.map((log, idx) => {
              const msg = log.message;
              const isCurrentStep = idx === currentStep - 1;

              return (
                <div
                  key={idx}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    isCurrentStep
                      ? 'border-indigo-600 bg-indigo-50'
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <span className="badge bg-gray-100 text-gray-700 text-xs">
                        Step {log.stepNumber}
                      </span>
                      <span className="text-xs text-gray-500">{log.timestamp}</span>
                    </div>
                    
                    <button
                      onMouseEnter={() => setShowTooltip(idx)}
                      onMouseLeave={() => setShowTooltip(null)}
                      className="relative"
                    >
                      <Info className="w-4 h-4 text-indigo-600 hover:text-indigo-700" />
                      
                      {/* Tooltip */}
                      {showTooltip === idx && (
                        <div className="absolute right-0 top-6 z-10 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-xl animate-fadeIn">
                          <div className="font-bold mb-1">💡 What's Happening:</div>
                          <div>{msg.explanation}</div>
                          <div className="absolute -top-2 right-2 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-900" />
                        </div>
                      )}
                    </button>
                  </div>

                  <div className="flex items-start space-x-3">
                    <div className="text-2xl">{msg.sender === 'client' ? '👤' : '⚡'}</div>
                    <div className="flex-1">
                      <div className="font-medium text-gray-900 mb-1">
                        {msg.task || msg.messageType}
                      </div>
                      <div className="text-sm text-gray-600 mb-2">
                        <span className="font-medium">{agentStates[msg.sender]?.name}</span>
                        {' → '}
                        <span className="font-medium">{agentStates[msg.receiver]?.name}</span>
                      </div>
                      
                      {/* Message Payload Preview */}
                      <details className="text-xs">
                        <summary className="cursor-pointer text-indigo-600 hover:text-indigo-700">
                          View message payload
                        </summary>
                        <div className="mt-2 bg-gray-900 rounded p-2 overflow-x-auto">
                          <pre className="text-gray-100 font-mono text-xs">
                            {JSON.stringify(msg.payload, null, 2)}
                          </pre>
                        </div>
                      </details>
                    </div>
                  </div>

                  {/* Learning Point */}
                  {msg.learningPoint && (
                    <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
                      <div className="flex items-start space-x-2">
                        <span>📚</span>
                        <div>
                          <span className="font-medium text-yellow-900">Learning Point: </span>
                          <span className="text-yellow-800">{msg.learningPoint}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Completion Message */}
      {currentStep >= totalSteps && messageLog.length > 0 && (
        <div className="card p-6 bg-gradient-to-r from-green-50 to-emerald-50 border-green-200 animate-fadeIn">
          <div className="flex items-start space-x-3">
            <div className="text-4xl">🎉</div>
            <div className="flex-1">
              <h3 className="font-bold text-gray-900 text-lg mb-2">Simulation Complete!</h3>
              <p className="text-gray-700 mb-4">
                You've successfully completed the <strong>{simulation.name}</strong> simulation.
                You watched {totalSteps} A2A messages being exchanged between agents.
              </p>
              
              {simulation.summary && (
                <div className="bg-white rounded-lg p-4 mb-4">
                  <div className="font-medium text-gray-900 mb-2">📊 Summary:</div>
                  <ul className="space-y-1 text-sm text-gray-700">
                    {simulation.summary.map((point, idx) => (
                      <li key={idx} className="flex items-start space-x-2">
                        <span>•</span>
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex space-x-3">
                <button onClick={handleReset} className="btn-primary">
                  Run Again
                </button>
                {onComplete && (
                  <button onClick={onComplete} className="btn-secondary">
                    Next Simulation
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SimulationEngine;

